import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Heading, Text, RadioCardField, BasicFormFieldProps } from '@frontend/design-system';
import { DurationTimeUnitSelection } from '../../components/duration-time-unit-selection';

type Props = {
  restrictionDurationFieldProps: Omit<BasicFormFieldProps<'number'>, 'label' | 'name'>;
  restrictionTimeUnitFieldProps: Omit<BasicFormFieldProps<'dropdown'>, 'label' | 'children' | 'name'>;
  rescheduleTypeFieldProps: Omit<BasicFormFieldProps<'radio'>, 'label' | 'name'>;
  restrictionErrorText?: string;
};

export const ReschedulingRules = ({
  restrictionDurationFieldProps,
  restrictionTimeUnitFieldProps,
  restrictionErrorText,
  rescheduleTypeFieldProps,
}: Props) => {
  const { t } = useTranslation('schedule-settings');

  return (
    <>
      <Heading level={2}>{t('Patient Cancellation & Rescheduling Rules')}</Heading>
      <Text color='light' css={{ marginBottom: 'var(--fabric-space-gap-lg)' }}>
        {t('Define how patients can cancel or change appointments via online booking, text, or the AI Receptionist.')}
      </Text>
      <section css={radioCardContainerStyle}>
        <RadioCardField
          {...rescheduleTypeFieldProps}
          name='rescheduleType'
          css={{ '& > div': { flexDirection: 'column' } }}
        >
          <RadioCardField.Option
            value='ANYTIME'
            title={t('Allow changes at any time')}
            css={radioCardOptionHoverStyle}
            description={t(
              'Patients can modify appointments up to the start time. Note: This may result in last-minute unfillable openings on your schedule.'
            )}
          />
          <RadioCardField.Option
            value='WITHIN_WINDOW'
            title={t('Set a restriction window (Recommended)')}
            css={radioCardOptionHoverStyle}
            description={t('Block automated cancellations and reschedules within:')}
            isInteractive={rescheduleTypeFieldProps.value === 'WITHIN_WINDOW'}
            selectionContent={
              <div css={{ marginTop: 'var(--fabric-space-gap-md)' }}>
                <DurationTimeUnitSelection
                  durationFieldProps={restrictionDurationFieldProps}
                  timeUnitFieldProps={restrictionTimeUnitFieldProps}
                  trailText={t('prior to the scheduled appointment')}
                  errorText={restrictionErrorText}
                />
              </div>
            }
          />
          <RadioCardField.Option
            value='OFFICE_CALL_ONLY'
            title={t('Block all automated changes')}
            css={radioCardOptionHoverStyle}
            description={t(
              'Patients cannot modify appointments digitally or via the AI Receptionist. All cancellations and reschedules must go through your front-desk staff.'
            )}
          />
        </RadioCardField>
      </section>
    </>
  );
};

const radioCardOptionHoverStyle = css({
  '&[aria-checked="true"]:hover': { backgroundColor: 'white' },
});

const radioCardContainerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--fabric-space-gap-md)',
});
